import { useTranslator } from '@nice-ccf/ui-controls';
import { ApiResponse } from './ccf-copilot-api';
import {
  addAdaptiveCard,
  addAdaptiveCardData,
  setCopilotConfig,
  setFinalSummary,
  setIndexConfig,
  setIsLiveCopilotEnabled,
  setIsRequestProcessing,
  removeAdaptiveCardByObjectId,
  AgentCopilotContent,
} from './ccf-copilot-chat/+state/ccf-live-copilot.slice';
import { copilotActionIcons, copilotSentimentIcons } from './ccf-copilot-icons';
import { globalActions } from '@nice-ccf/ui-components';
import * as ACData from 'adaptivecards-templating';
import { allAdaptiveCardSchemas } from './ccf-copilot-schemas';

/**
 * Shared prefix for Task Assist adaptive-card object ids. Kept in sync with the
 * value used when the intent card is created (ccf-copilot-chat-suggestion-card).
 */
export const INTENT_OBJECT_ID_PREFIX =
  'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_';

/**
 * Build a stable, per-Task-Assist object id from its title so each Task Assist
 * card has a unique identity (the title is available both when the card is
 * created and when it is completed via auto-run or manual submit).
 * @example intentCardObjectId('Book tickets')
 */
export const intentCardObjectId = (title: string) =>
  `${INTENT_OBJECT_ID_PREFIX}intent_${(title ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;

/** Object id for the "completed by Task Assist" card matching a given title. */
export const intentSuccessObjectId = (title: string) =>
  `${INTENT_OBJECT_ID_PREFIX}intentSuccess_${(title ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;
/**
 * enum for actions icons
 */
enum ActionIconType {
  LINK_ICON = 'AdaptiveCardLinkIcon',
  OPEN_LINK_ICON = 'AdaptiveCardOpenLinkIcon',
  EXPAND_ICON = 'AdaptiveCardExpandIcon',
  SEND_ICON = 'AdaptiveCardSendIcon',
  UP_ICON = 'AdaptiveCardUpArrowIcon',
  DOWN_ICON = 'AdaptiveCardDownArrowIcon',
  INSERT_ICON = 'AdaptiveCardInsertIcon',
  IMAGE_PLACEHOLDER_ICON = 'AdaptiveCardImageIcon',
  SENT_ICON = 'AdaptiveCardSentIcon',
  PROCESS_STEPS_ICON = 'AdaptiveCardProcessStepsIcon',
  SEARCH_ICON = 'AdaptiveCardSearchIcon',
  REMOVE_ICON = 'AdaptiveCardRemoveIcon',
  LIKE_ICON = 'AdaptiveCardLikeIcon',
  DISLIKE_ICON = 'AdaptiveCardDislikeIcon',
  LIKE_CLICKED_ICON = 'AdaptiveCardLikeClickedIcon',
  DISLIKE_CLICKED_ICON = 'AdaptiveCardDislikeClickedIcon',
  INTERNAL_USE_ICON = 'AdaptiveCardInternalUseIcon',
  SPARKLE_ICON = 'AdaptiveCardSparkleIcon',
}

/**
 * to process api response
 * @param response - response
 * @example - `processApiResponse(response)`
 */
export function processApiResponse(
  response: ApiResponse,
  dispatch: any,
  mediaType = 'Digital',
  ignore = false,
  showGreenConfidenceDot = false
) {
  console.log(new Date().toISOString(), 'inside process api response');
  if (response?.acpAppElements) {
    response.acpAppElements.forEach((element: any) => {
      const { contactId, utcTimestamp } = response;
      const formattedResponse = {
        agentAssistSource: response.agentAssistSource,
        contactId,
        utcTimestamp: new Date(utcTimestamp),
        acpAppElements: [element],
        responseSent: '',
        isResponseInserted: false,
        responseInserted: '',
        isResponseSent: false,
        isJourneySummaryExpanded: false,
      };
      // const copilotData = formattedResponse;
      // const copilotMessageData = {
      //   ...copilotData,
      //   acpAppElements: [element],
      // };
      const adaptiveCardToAdd = {
        [contactId]: formattedResponse,
      };
      let { contentType } = element;
      const { content } = element;
      const modifiedContentType = `${contentType}_Voice`;
      contentType = mediaType === 'Voice' ? modifiedContentType : contentType;
      switch (contentType) {
        case AgentCopilotContent.SENTIMENT: {
          let urlIcon = '';
          if (typeof content !== 'object') {
            urlIcon = JSON.parse(content)?.icon;
          }

          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [urlIcon],
            dispatch,
            copilotSentimentIcons
          );
          break;
        }
        case AgentCopilotContent.SALES_OFFER:
        case AgentCopilotContent.SALES_OFFER_VOICE:
        case AgentCopilotContent.KB_COMBO:
        case AgentCopilotContent.KB_COMBO_EMAIL:
        case AgentCopilotContent.KB_COMBO_VOICE: {
          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [
              ActionIconType.INSERT_ICON,
              ActionIconType.SEND_ICON,
              ActionIconType.LINK_ICON,
              ActionIconType.OPEN_LINK_ICON,
              ActionIconType.IMAGE_PLACEHOLDER_ICON,
              ActionIconType.EXPAND_ICON,
              ActionIconType.SENT_ICON,
              ActionIconType.PROCESS_STEPS_ICON,
              ActionIconType.LIKE_ICON,
              ActionIconType.DISLIKE_ICON,
              ActionIconType.LIKE_CLICKED_ICON,
              ActionIconType.DISLIKE_CLICKED_ICON,
              ActionIconType.INTERNAL_USE_ICON,
            ],
            dispatch,
            copilotActionIcons,
            ignore,
            showGreenConfidenceDot
          );
          break;
        }
        // case AgentCopilotContent.FINAL_SUMMARY:
        case AgentCopilotContent.RT_SUMMARY:
        case AgentCopilotContent.FINAL_SUMMARY_NOTES: {
          // Add card data to existing state
          dispatch(addAdaptiveCardData(adaptiveCardToAdd));
          break;
        }
        case AgentCopilotContent.INTENT_ASSISTANCE:
        case AgentCopilotContent.INTENT_INPROGRESS: {
          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [
              ActionIconType.INSERT_ICON,
              ActionIconType.SEND_ICON,
              ActionIconType.SENT_ICON,
            ],
            dispatch,
            copilotActionIcons
          );
          break;
        }
        case AgentCopilotContent.COMPLIANCE_CARD:
        case AgentCopilotContent.COMPLIANCE_CARD_VOICE: {
          const responsesToAdd = {
            [formattedResponse.contactId]: formattedResponse,
          };
          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [
              ActionIconType.INSERT_ICON,
              ActionIconType.SEND_ICON,
              ActionIconType.SENT_ICON,
            ],
            dispatch,
            copilotActionIcons
          );
          // Add compliance to state
          //dispatch(setComplianceCard(responsesToAdd));
          break;
        }
        // case AgentCopilotContent.CONVERSATION_ASSISTANCE: {
        //   const responsesToAdd = {
        //     [formattedResponse.contactId]: formattedResponse,
        //   };
        //   // dispatch(setConversationAssistance(responsesToAdd));
        //   dispatch(addAdaptiveCardData(adaptiveCardToAdd));
        //   break;
        // }
        case AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED:
        case AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED_LIVE: {
          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [ActionIconType.SPARKLE_ICON],
            dispatch,
            copilotActionIcons
          );
          break;
        }
        case AgentCopilotContent.EMAIL_RESPONSE_GENERATED:
        case AgentCopilotContent.EMAIL_RESPONSE_GENERATED_LIVE: {
          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [],
            dispatch,
            copilotActionIcons,
            true
          );
          break;
        }
        case AgentCopilotContent.CONVERSATION_ASSISTANCE: {
          ensureCustomerConcernProperty(formattedResponse);
          const adaptiveCardToAddforConversationAssistance = {
            [formattedResponse.contactId]: formattedResponse,
          };
          addAdaptiveCard(
            contactId,
            adaptiveCardToAddforConversationAssistance,
            [
              ActionIconType.SEARCH_ICON,
              ActionIconType.REMOVE_ICON,
              ActionIconType.SENT_ICON,
            ],
            dispatch,
            copilotActionIcons
          );
          //dispatch(setConversationAssistance(responsesToAdd));
          break;
        }
        case AgentCopilotContent.JOURNEY_SUMMARY:
        case AgentCopilotContent.AGENT_TRANSFER:
        case AgentCopilotContent.AUTOPILOT_TRANSFER:
        case AgentCopilotContent.INTENT_COMPLETED:
        case AgentCopilotContent.FEEDBACK: {
          const adaptiveCardToAddforConversationAssistance = {
            [formattedResponse.contactId]: formattedResponse,
          };
          addAdaptiveCard(
            contactId,
            adaptiveCardToAddforConversationAssistance,
            [
              ActionIconType.LIKE_ICON,
              ActionIconType.LIKE_CLICKED_ICON,
              ActionIconType.DISLIKE_ICON,
              ActionIconType.DISLIKE_CLICKED_ICON,
            ],
            dispatch,
            copilotActionIcons
          );
          break;
        }
        case AgentCopilotContent.BEHAVIORAL_GUIDANCE: {
          const responsesToAdd = {
            [formattedResponse.contactId]: formattedResponse,
          };
          addAdaptiveCard(
            contactId,
            adaptiveCardToAdd,
            [
              ActionIconType.INSERT_ICON,
              ActionIconType.SEND_ICON,
              ActionIconType.SENT_ICON,
            ],
            dispatch,
            copilotActionIcons
          );
          // Add compliance to state
          //dispatch(setComplianceCard(responsesToAdd));
          break;
        }
      }
    });
  }
  console.log(new Date().toISOString(), 'existing process api response');
  // dispatch(setIsRequestProcessing(false));
}

/**
 * to ensure customer Concern Property is present
 * @param response - response
 * @example - `ensureCustomerConcernProperty(response)`
 */
function ensureCustomerConcernProperty(response: any) {
  response.acpAppElements.forEach((element: any) => {
    const contentObj = JSON.parse(element.content);

    if (contentObj && Array.isArray(contentObj.customerQuestionsConcerns)) {
      contentObj.customerQuestionsConcerns.forEach((item: any) => {
        // eslint-disable-next-line no-prototype-builtins
        if (
          // eslint-disable-next-line no-prototype-builtins
          !item.hasOwnProperty('customerConcern') ||
          item.customerConcern === 'None' ||
          item.customerConcern === 'N/A'
        ) {
          item.customerConcern = '';
        }
      });
    }

    element.content = JSON.stringify(contentObj);
  });
}

/**
 * to process api response
 * @param response - response
 * @example - `processFinalSummary(response, dispatch, conversationTopics)`
 */
export function processFinalSummary(
  response: any,
  dispatch: any,
  conversationTopics: string[],
  activeContactId: string
) {
  const finalSummaryObject =
    response?.acpAppElements?.find(
      (element: any) => element.contentType === 'finalSummary'
    ) ?? null;
  if (finalSummaryObject) {
    const finalSummaryContent = JSON.parse(finalSummaryObject?.content);
    const dispositionNotes = {
      summary: finalSummaryContent?.summary,
      resolved: finalSummaryContent?.isResolved === 'true' ? true : false,
      intents: finalSummaryContent?.chatReasons,
      actions: finalSummaryContent?.chatAgentTasksPerformed,
      outcomes: finalSummaryContent?.chatFinalOutcome,
      entities: finalSummaryContent?.chatEntitiesMentioned ?? '',
    };
    dispatch(setFinalSummary(dispositionNotes));
    conversationTopics.length > 0 &&
      handleFeedbackData(
        response.contactId,
        conversationTopics,
        dispatch,
        activeContactId
      );
  }
}

/**
 * to process api response
 * @param response - response
 * @example - `saveCopilotConfig(response, dispatch)`
 */
export function saveCopilotConfig(updatedConfig: any, dispatch: any) {
  localStorage.setItem('agentAssistAppConfig', JSON.stringify(updatedConfig))
  dispatch(setCopilotConfig(updatedConfig));
}

/**
 * to process api response
 * @param response - response
 * @example - `saveIndexConfig(response, dispatch)`
 */
export function saveIndexConfig(updatedConfig: any, dispatch: any) {
  dispatch(setIndexConfig(updatedConfig));
  dispatch(globalActions.setLocale(updatedConfig?.language));
}

/**
 * to process api response
 * @param response - response
 * @example - `processConversationAssistance(response, dispatch)`
 */
export function processConversationAssistance(response: any, dispatch: any) {
  const conversationData = JSON.parse(response ?? '{}');
  if (conversationData && conversationData.CustomerQuestionsAndConcerns) {
    const customerQuestionsAndConcerns =
      conversationData.CustomerQuestionsAndConcerns.map(
        (conversationItem: any) => {
          return {
            customerConcern: conversationItem.customerConcern,
            customerQuestionOrStatement:
              conversationItem.customerQuestionOrStatement,
            IsConcernResolvedByAgent: conversationItem.IsConcernResolvedByAgent,
            CSVStepsFollowedByAgent: conversationItem.CSVStepsFollowedByAgent,
          };
        }
      );
    //dispatch(updateCopilotConversationAssistance(customerQuestionsAndConcerns));
  }
}

/**
 * to process api response
 * @param response - response
 * @example - `processSalesAssistance(response, dispatch)`
 */
export function processSalesAssistance(response: any, dispatch: any) {
  const salesData = response ?? '{}';
  if (salesData) {
    const conversationContent = JSON.parse(salesData);
    const customerQuestionsAndConcerns = {
      customerConcern:
        conversationContent?.CustomerQuestionsAndConcerns?.customerConcern,
      customerQuestionOrStatement:
        conversationContent?.CustomerQuestionsAndConcerns
          ?.customerQuestionOrStatement,
      IsConcernResolvedByAgent:
        conversationContent?.CustomerQuestionsAndConcerns
          ?.IsConcernResolvedByAgent,
      CSVStepsFollowedByAgent:
        conversationContent?.CustomerQuestionsAndConcerns
          ?.CSVStepsFollowedByAgent,
    };
    //dispatch(updateCopilotConversationAssistance(customerQuestionsAndConcerns));
  }
}

/**
 * to process api response
 * @param response - response
 * @example - `setIsCopilotEnabled(response, dispatch)`
 */
export function setIsCopilotEnabled(updatedConfig: any, dispatch: any) {
  dispatch(setIsLiveCopilotEnabled(updatedConfig));
}

/**
 *
 * @param response - response
 * @example - `setIsRequestProcessing(response, dispatch)`
 */
export function saveIsRequestProcessing(value: boolean, dispatch: any) {
  dispatch(setIsRequestProcessing(value));
}

/**
 * Function to handle interaction summary
 * @example - handleJourneySummaryData()
 * @returns
 */
export const handleJourneySummaryData = (
  activecontact: any,
  previousInteractionSummary: string,
  perviousInteractionData: any,
  dispatch: any
) => {
  const records = {
    interactionsData: perviousInteractionData,
    summary: previousInteractionSummary,
  };

  const templateJson =
    allAdaptiveCardSchemas[AgentCopilotContent.JOURNEY_SUMMARY];

  const template = new ACData.Template(templateJson);
  const contextForAdaptiveCard = {
    $root: { ...records },
  };
  const copilotCard = template.expand(contextForAdaptiveCard);

  const response = {
    agentAssistSource: 'Illuminate',
    contactId: `${activecontact?.id}`,
    utcTimestamp: '2024-04-23T04:27:23.455584Z',
    acpAppElements: [
      {
        cardType: 'AdaptiveCard',
        contentType: 'journeySummary',
        // eslint-disable-next-line no-useless-escape
        content: records,
        objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_journeySummary',
      },
    ],
    type: null,
  } as unknown as ApiResponse;
  processApiResponse(response, dispatch);
  return;
};

/**
 * Function to handle interaction summary
 * @example - handleIntentComplete()
 * @returns
 */
export const handleIntentComplete = (
  id: any,
  data: any,
  dispatch: any,
  intentTitle?: string
) => {
  const templateJson =
    allAdaptiveCardSchemas[AgentCopilotContent.INTENT_COMPLETED];

  const template = new ACData.Template(templateJson);
  const contextForAdaptiveCard = {
    $root: { ...data },
  };
  const copilotCard = template.expand(contextForAdaptiveCard);

  const response = {
    agentAssistSource: 'Illuminate',
    contactId: `${id}`,
    utcTimestamp: '2024-04-23T04:27:23.455584Z',
    acpAppElements: [
      {
        cardType: 'AdaptiveCard',
        contentType: 'intentSuccess',
        // eslint-disable-next-line no-useless-escape
        content: data,
        // Unique per Task Assist so multiple completed cards can coexist.
        objectId: intentTitle
          ? intentSuccessObjectId(intentTitle)
          : `${INTENT_OBJECT_ID_PREFIX}intent`,
      },
    ],
    type: null,
  } as unknown as ApiResponse;
  processApiResponse(response, dispatch);
  return;
};

/**
 * Function to handle autopilot Summary
 * @example - handleAutopilotSummary()
 * @returns
 */
export const handleAutopilotSummary = (
  activecontact: any,
  autopilotSummary: string,
  dispatch: any,
  translate: any
) => {
  const content = {
    title: translate('autopilotTransferSummary'),
    description: autopilotSummary,
  };
  const templateJson =
    allAdaptiveCardSchemas[AgentCopilotContent.AUTOPILOT_TRANSFER];

  const response = {
    agentAssistSource: 'Illuminate',
    contactId: `${activecontact?.id}`,
    utcTimestamp: '2024-04-23T04:27:23.455584Z',
    acpAppElements: [
      {
        cardType: 'AdaptiveCard',
        contentType: 'autopilotTransfer',
        // eslint-disable-next-line no-useless-escape
        content: content,
        objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_autopilotTransfer',
      },
    ],
    type: null,
  } as unknown as ApiResponse;
  processApiResponse(response, dispatch);
  return;
};

/**
 * Function to handle agent transfer Summary
 * @example - handleAgentTransferSummary()
 * @returns
 */
export const handleAgentTransferSummary = (
  activecontact: any,
  agentTransferSummary: string,
  dispatch: any,
  translate: any
) => {
  const content = {
    title: translate('agentTransferSummary'),
    description: agentTransferSummary,
  };
  const templateJson =
    allAdaptiveCardSchemas[AgentCopilotContent.AGENT_TRANSFER];

  const response = {
    agentAssistSource: 'Illuminate',
    contactId: `${activecontact?.id}`,
    utcTimestamp: '2024-04-23T04:27:23.455584Z',
    acpAppElements: [
      {
        cardType: 'AdaptiveCard',
        contentType: 'agentTransfer',
        // eslint-disable-next-line no-useless-escape
        content: content,
        objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_agentTransfer',
      },
    ],
    type: null,
  } as unknown as ApiResponse;
  processApiResponse(response, dispatch);
  return;
};

/**
 * Function to handle inserting intent messages
 * @param caseId - any
 * @param intents - any
 * @param userReplyChatData - any
 * @param dispatch - any
 * @example
 * ```
 * handleSubmitIntentMessages(data, handleIconChange, handleExpandImageClick, dispatch)
 * ```
 */
export const handleSubmitIntentMessages = async (
  caseId: any,
  cardId: any,
  text: any,
  dispatch: any,
  translate: any
) => {
  // Remove ONLY this Task Assist's interactive card (keyed by its title-derived
  // objectId). The "completed by Task Assist" marker added below has a
  // different objectId, so it stays; sibling Task Assist cards are untouched.
  dispatch(
    removeAdaptiveCardByObjectId({
      caseId,
      objectId: intentCardObjectId(text),
    })
  );

  const data = {
    text: text,
    title: translate('completedByTaskAssist'),
  };
  handleIntentComplete(caseId, data, dispatch, text);
};

/**
 * Function to handle feedback data
 * @example - handleFeedbackData()
 * @returns
 */
export const handleFeedbackData = (
  activecontact: any,
  conversationTopics: string[],
  dispatch: any,
  activeContactId: string
) => {
  const content = conversationTopics;

  const response = {
    agentAssistSource: 'Illuminate',
    contactId: `${activeContactId}`,
    utcTimestamp: '2024-04-23T04:27:23.455584Z',
    acpAppElements: [
      {
        cardType: 'AdaptiveCard',
        contentType: 'feedback',
        // eslint-disable-next-line no-useless-escape
        content: { conversationTopics: content },
        objectId: 'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_feedback',
      },
    ],
    type: null,
  } as unknown as ApiResponse;
  processApiResponse(response, dispatch);
  return;
};
